import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClientAddress, clientsApi } from "@/lib/api";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";

export const orderShippingAddressesBaseQueryKey = ["order-shipping-addresses"] as const;
export const orderShippingAddressesQueryKey = (clientCode?: string) => [...orderShippingAddressesBaseQueryKey, clientCode] as const;

interface OrderAddresses {
  shipping: ClientAddress[];
  billing: ClientAddress | null;
}

/**
 * Endereços de entrega ativos da escola (padrão primeiro) e o escolhido para o pedido. Sem escolha, vale o padrão;
 * ao trocar de escola a escolha anterior deixa de valer. Devolve também o padrão de cobrança, que vai no PDF do
 * pedido como endereço de faturamento. Com a flag desligada não busca nada e não há endereço.
 */
export function useOrderShippingAddress(clientCode?: string) {
  const [choice, setChoice] = useState<{ clientCode: string; addressId: number } | null>(null);

  const query = useQuery<OrderAddresses>({
    queryKey: orderShippingAddressesQueryKey(clientCode),
    queryFn: async () => {
      // GET /clients/code/{code} devolve os endereços com a flag ligada (o ApiClient já desembrulha o corpo).
      const client = await clientsApi.getByCode(clientCode!);
      return {
        shipping: (client?.addresses ?? [])
          .filter((address) => address.addressType === "Shipping" && address.isActive)
          .sort((a, b) => Number(b.isDefault) - Number(a.isDefault)),
        billing: client?.defaultBillingAddress ?? null,
      };
    },
    enabled: isCustomerGroupAddressEnabled && !!clientCode,
    // O App desliga o refetch ao montar e ao focar a janela; aqui a lista precisa refletir endereços cadastrados
    // depois (aba "Endereços" do cliente, outra aba do navegador).
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const addresses = query.data?.shipping ?? [];
  const chosen = choice?.clientCode === clientCode
    ? addresses.find((address) => address.id === choice.addressId)
    : undefined;
  const selectedAddress = chosen ?? addresses.find((address) => address.isDefault) ?? addresses[0] ?? null;

  return {
    addresses,
    selectedAddress,
    billingAddress: query.data?.billing ?? null,
    selectAddress: (addressId: number) => clientCode && setChoice({ clientCode, addressId }),
    isLoading: query.isLoading && query.fetchStatus !== "idle",
    isError: query.isError,
  };
}
